/**
 * Description search for Tennessee specialty plates.
 * No FTS / embeddings — token + synonym scoring over a few hundred rows.
 */

export const TN_SEARCH_STOPWORDS = new Set([
	'a',
	'an',
	'the',
	'with',
	'plate',
	'plates',
	'tag',
	'tags',
	'license',
	'on',
	'of',
	'for',
	'and',
	'or',
	'in',
	'to',
	'from',
	'me',
]);

/** Query token → extra tokens to look for in search_text. */
export const TN_SEARCH_SYNONYMS = {
	snake: ['rattlesnake', 'gadsden', 'tread', 'dtom'],
	rattlesnake: ['snake', 'gadsden', 'tread'],
	gadsden: ['snake', 'rattlesnake', 'tread', 'sycamore', 'dtom'],
	dtom: ['gadsden', 'tread', 'snake'],
	tread: ['gadsden', 'snake', 'dtom'],
	yellow: ['gold', 'gadsden'],
	black: ['millennial'],
	bear: ['smokies', 'smoky', 'grizzlies'],
	smokies: ['smoky', 'bear'],
	smoky: ['smokies', 'bear'],
	bee: ['bees', 'honeybee', 'honey'],
	bees: ['bee', 'honeybee'],
	honeybee: ['bee', 'bees'],
	vol: ['vols', 'volunteers', 'knoxville'],
	vols: ['vol', 'volunteers', 'knoxville'],
	volunteers: ['vol', 'vols', 'knoxville'],
	dolly: ['dollywood', 'parton', 'imagination'],
	parton: ['dolly', 'dollywood'],
	ag: ['agriculture', 'farm', 'barn'],
	farm: ['agriculture', 'barn', 'ag'],
	barn: ['agriculture', 'farm'],
	igwt: ['god', 'trust'],
	god: ['trust', 'igwt'],
	tristar: ['stars', 'standard', 'blue'],
	standard: ['automobile', 'regular', 'blue'],
	nra: ['rifle'],
	preds: ['predators'],
	predators: ['preds'],
	titans: ['nfl'],
	bama: ['alabama', 'crimson'],
	vandy: ['vanderbilt'],
	pow: ['prisoner'],
	usmc: ['marines', 'marine'],
	marines: ['usmc', 'marine'],
	usaf: ['airforce', 'air'],
	ussf: ['space'],
};

export function normalizeSearchText(value) {
	return String(value || '')
		.toLowerCase()
		.replace(/['’]/g, '')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();
}

/**
 * @param {string} query
 * @returns {string[]}
 */
export function tokenizeQuery(query) {
	const raw = normalizeSearchText(query);
	if (!raw) return [];
	return raw.split(/\s+/).filter((t) => t && !TN_SEARCH_STOPWORDS.has(t) && t.length > 1);
}

/**
 * @param {string} query
 * @returns {{ tokens: string[], expanded: string[] }}
 */
export function expandQueryTokens(query) {
	const tokens = tokenizeQuery(query);
	const expanded = new Set(tokens);
	for (const token of tokens) {
		const extras = TN_SEARCH_SYNONYMS[token];
		if (extras) extras.forEach((e) => expanded.add(e));
	}
	return { tokens, expanded: [...expanded] };
}

function haystackForPlate(plate) {
	if (plate.searchText || plate.search_text) {
		return normalizeSearchText(plate.searchText || plate.search_text);
	}
	const aliases = plate.aliases || [];
	const visual = plate.visualKeywords || plate.visual_keywords || [];
	return normalizeSearchText(
		[
			plate.officialName || plate.official_name,
			plate.commonName || plate.common_name,
			plate.category,
			...(Array.isArray(aliases) ? aliases : []),
			...(Array.isArray(visual) ? visual : []),
		].join(' '),
	);
}

function aliasList(plate) {
	const aliases = plate.aliases;
	if (Array.isArray(aliases)) return aliases.map((a) => normalizeSearchText(a));
	if (typeof aliases === 'string') {
		try {
			const parsed = JSON.parse(aliases);
			if (Array.isArray(parsed)) return parsed.map((a) => normalizeSearchText(a));
		} catch {
			/* ignore */
		}
	}
	return [];
}

/**
 * Higher is better. Empty query scores 0 (caller should keep original order).
 * @param {object} plate
 * @param {string} query
 */
export function scoreTnPlate(plate, query) {
	const { tokens, expanded } = expandQueryTokens(query);
	if (!tokens.length) return 0;

	const hay = haystackForPlate(plate);
	const aliases = aliasList(plate);
	const official = normalizeSearchText(plate.officialName || plate.official_name || '');
	const common = normalizeSearchText(plate.commonName || plate.common_name || '');
	const category = normalizeSearchText(plate.category || '');
	const qNorm = normalizeSearchText(query);

	let score = 0;

	if (qNorm && aliases.some((a) => a && (a === qNorm || a.includes(qNorm) || qNorm.includes(a)))) {
		score += 120;
	}
	if (qNorm && (official === qNorm || common === qNorm)) {
		score += 90;
	}

	const allOriginalHit = tokens.every((t) => hay.includes(t));
	if (allOriginalHit) score += 50 + tokens.length * 10;

	for (const token of tokens) {
		if (hay.includes(token)) score += 10;
		if (official.includes(token) || common.includes(token)) score += 8;
		if (aliases.some((a) => a.includes(token))) score += 18;
		if (category.includes(token)) score += 4;
	}

	for (const token of expanded) {
		if (tokens.includes(token)) continue;
		if (hay.includes(token)) score += 6;
	}

	return score;
}

export const TN_COLOR_PRESETS = [
	{ id: 'all', label: 'All colors', mode: 'all' },
	{ id: 'red', label: 'Red', hueMin: 345, hueMax: 18, swatch: '#c62828' },
	{ id: 'orange', label: 'Orange', hueMin: 18, hueMax: 42, swatch: '#ef6c00' },
	{ id: 'yellow', label: 'Yellow', hueMin: 42, hueMax: 72, swatch: '#f4c400' },
	{ id: 'green', label: 'Green', hueMin: 72, hueMax: 160, swatch: '#2e7d32' },
	{ id: 'blue', label: 'Blue', hueMin: 160, hueMax: 255, swatch: '#1565c0' },
	{ id: 'purple', label: 'Purple', hueMin: 255, hueMax: 345, swatch: '#6a1b9a' },
	{ id: 'neutral', label: 'White / gray', mode: 'neutral', swatch: '#cfd8dc' },
];

export function tnPlateIsNeutral(plate) {
	const s = Number(plate?.avgSat);
	const l = Number(plate?.avgLight);
	if (!Number.isFinite(s)) return true;
	if (s < 0.18) return true;
	if (Number.isFinite(l) && l >= 0.78 && s < 0.28) return true;
	return false;
}

export function hueInRange(hue, min, max) {
	const h = ((Number(hue) % 360) + 360) % 360;
	const a = Number(min);
	const b = Number(max);
	if (!Number.isFinite(a) || !Number.isFinite(b)) return true;
	if (b - a >= 359) return true;
	if (a <= b) return h >= a && h <= b;
	return h >= a || h <= b;
}

export function plateMatchesColorFilter(plate, colorFilter) {
	if (!colorFilter || colorFilter.mode === 'all') return true;
	if (colorFilter.mode === 'neutral') return tnPlateIsNeutral(plate);
	if (tnPlateIsNeutral(plate)) return Boolean(colorFilter.includeNeutrals);
	return hueInRange(plate.avgHue, colorFilter.hueMin, colorFilter.hueMax);
}

export function compareTnPlatesByColor(a, b) {
	const aNeutral = tnPlateIsNeutral(a);
	const bNeutral = tnPlateIsNeutral(b);
	if (aNeutral !== bNeutral) return aNeutral ? 1 : -1;
	if (!aNeutral) {
		const dh = (Number(a.avgHue) || 0) - (Number(b.avgHue) || 0);
		if (dh) return dh;
	}
	const dl = (Number(a.avgLight) || 0) - (Number(b.avgLight) || 0);
	if (dl) return dl;
	return (a.displayOrder || 0) - (b.displayOrder || 0);
}

/**
 * @template T
 * @param {T[]} plates
 * @param {string} [query]
 * @param {string} [category]
 * @param {{ mode?: string, hueMin?: number, hueMax?: number, includeNeutrals?: boolean } | null} [colorFilter]
 * @returns {T[]}
 */
export function filterAndRankTnPlates(plates, query = '', category = '', colorFilter = null) {
	const list = Array.isArray(plates) ? plates : [];
	const cat = String(category || '').trim();
	const byCategory = cat
		? list.filter((p) => String(p.category || '') === cat)
		: list;
	const filtered = byCategory.filter((p) => plateMatchesColorFilter(p, colorFilter));
	const q = String(query || '').trim();
	if (!q) return [...filtered].sort(compareTnPlatesByColor);
	return filtered
		.map((plate) => ({ plate, score: scoreTnPlate(plate, q) }))
		.filter((row) => row.score > 0)
		.sort(
			(a, b) =>
				b.score - a.score || compareTnPlatesByColor(a.plate, b.plate),
		)
		.map((row) => row.plate);
}

/**
 * Rebuild search_text after admin alias/keyword edits.
 * @param {{ officialName?: string, official_name?: string, commonName?: string, common_name?: string, category?: string, aliases?: string[], visualKeywords?: string[], visual_keywords?: string[] }} plate
 */
export function buildTnPlateSearchText(plate) {
	const aliases = plate.aliases || [];
	const visual = plate.visualKeywords || plate.visual_keywords || [];
	const parts = [
		plate.officialName || plate.official_name,
		plate.commonName || plate.common_name,
		plate.category,
		...aliases,
		...visual,
	];
	return [...new Set(parts.map((s) => String(s || '').toLowerCase()).filter(Boolean))].join(' ');
}
